import React from 'react';
import { Card, SubjectType } from '../../types/Card';

interface CardPreviewProps {
  card: Card;
  onPin: () => void;
  onRegenerate: () => void;
  onEdit: () => void;
  onFlag?: () => void;
}

const SUBJECT_CONFIG: Record<SubjectType, { color: string; emoji: string; label: string }> = {
  english:   { color: '#2E86AB', emoji: '📚', label: 'English' },
  maths:     { color: '#DC143C', emoji: '🔢', label: 'Mathematics' },
  science:   { color: '#4A90E2', emoji: '🔬', label: 'Science' },
  history:   { color: '#8B4513', emoji: '🏛️', label: 'History' },
  geography: { color: '#228B22', emoji: '🌍', label: 'Geography' },
  computing: { color: '#E4A101', emoji: '💻', label: 'Computing' },
  art:       { color: '#FF8C00', emoji: '🎨', label: 'Art & Design' },
  music:     { color: '#9C27B0', emoji: '🎵', label: 'Music' },
  pe:        { color: '#E91E63', emoji: '⚽', label: 'P.E.' },
  dt:        { color: '#795548', emoji: '🔧', label: 'Design & Technology' },
  re:        { color: '#607D8B', emoji: '🕊️', label: 'R.E.' },
  pshe:      { color: '#00BCD4', emoji: '💬', label: 'PSHE' },
  mfl:       { color: '#3F51B5', emoji: '🌐', label: 'Modern Languages' },
  space:     { color: '#9370DB', emoji: '🚀', label: 'Space' },
};

export const CardPreview: React.FC<CardPreviewProps> = ({
  card,
  onPin,
  onRegenerate,
  onEdit,
  onFlag,
}) => {
  const subjectConfig = SUBJECT_CONFIG[card.subject];
  const [selected, setSelected] = React.useState(
    card.selectedPrompts || { question: 0, wonder: 0, investigate: 0 }
  );

  const getPromptText = (type: 'question' | 'wonder' | 'investigate') => {
    const val = card.prompts[type];
    if (Array.isArray(val)) return val[selected[type]] || val[0];
    return val;
  };

  const isMulti = (type: 'question' | 'wonder' | 'investigate') => Array.isArray(card.prompts[type]) && (card.prompts[type] as string[]).length > 1;

  const cycleOption = (type: 'question' | 'wonder' | 'investigate') => {
    const arr = card.prompts[type] as string[];
    setSelected(s => ({ ...s, [type]: (s[type] + 1) % arr.length }));
  };

  return (
    <div 
      className="card-preview"
      style={{ borderLeftColor: subjectConfig.color }}
    >
      <div className="card-header">
        <div className="card-subject">
          <span className="card-emoji">{subjectConfig.emoji}</span>
          <span 
            className="card-subject-label"
            style={{ color: subjectConfig.color }}
          >
            {subjectConfig.label}
          </span>
        </div>
        <div className="card-actions">
          <button onClick={onPin} className={`pin-button ${card.isPinned ? 'pinned' : ''}`} title={card.isPinned ? 'Unpin card' : 'Pin card'} aria-label={card.isPinned ? 'Unpin card' : 'Pin card'}>📌</button>
          <button onClick={onRegenerate} className="regenerate-button" title="Regenerate prompts" aria-label="Regenerate prompts">🔄</button>
          <button onClick={onEdit} className="edit-button" title="Edit card" aria-label="Edit card">✏️</button>
          {onFlag && <button onClick={onFlag} className="flag-button" title="Flag for review" aria-label="Flag for review">🚩</button>}
        </div>
      </div>

      <h3 className="card-topic">{card.topic}</h3>

      <div className="card-prompts">
        {(['question', 'wonder', 'investigate'] as const).map(type => (
          <div key={type} className="prompt-item">
            <div className="prompt-label">
              {type === 'question' ? 'Question Generator' : type === 'wonder' ? 'Wondering' : 'Investigation'}
              {isMulti(type) && (
                <button className="prompt-cycle" onClick={() => cycleOption(type)} title="See another option">
                  ↻ {selected[type] + 1}/{(card.prompts[type] as string[]).length}
                </button>
              )}
            </div>
            <div className="prompt-text">{getPromptText(type)}</div>
          </div>
        ))}
      </div>

      {card.isPinned && (
        <div className="pinned-indicator">
          <span>📌 Pinned - excluded from bulk regeneration</span>
        </div>
      )}
    </div>
  );
};
